import {
  presetLabel,
  rangeDays,
  validateRangeSelection,
  type RangePreset,
} from '@anytracker/core'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Segmented, Tabs, type Choice } from '../components/Layout.js'
import { formatDay } from '../format/dates.js'
import styles from './Range.module.css'
import { useRange } from './RangeProvider.js'
import { readRangeParams, writeRangeParams } from './url.js'

/** The four periods the design puts up front; the rest sit under "Other dates". */
const MAIN: readonly RangePreset[] = ['last7', 'last30', 'last90', 'allTime']
const OTHER: readonly RangePreset[] = ['thisMonth', 'lastMonth']

/** The short form shown on the control. Always contained in `presetLabel`. */
function shortLabel(preset: RangePreset): string {
  switch (preset) {
    case 'last7':
      return '7 days'
    case 'last30':
      return '30 days'
    case 'last90':
      return '90 days'
    case 'allTime':
      return 'All time'
    default:
      return presetLabel(preset)
  }
}

/**
 * The shared date range control (T-402).
 *
 * Two things it always shows, both deliberate:
 *
 *  - **The resolved dates, next to the preset name.** "Last 30 days" on a chart
 *    you are looking at now is fine; on an export opened six weeks later it is
 *    meaningless. Keeping the words and the dates together here means they are
 *    never separated, and T-504 carries the same pair into the report.
 *  - **How many days that is.** The difference between a 7-day and a 90-day
 *    window is the difference between a shape and a trend, and nothing else on
 *    a chart says which one you are reading.
 *
 * The four common periods are always visible. This month, last month and
 * exact dates are one tap further, under "Other dates", which opens by itself
 * when one of them is the current choice so the choice is never hidden.
 */
export function RangeControl({
  empty = false,
  look = 'tabs',
}: {
  readonly empty?: boolean
  /** Underlined tabs (Insights) or a segmented band (Export, filters). */
  readonly look?: 'tabs' | 'segmented'
}) {
  const { selection, setSelection, resolved } = useRange()
  const [search, setSearch] = useSearchParams()
  const [custom, setCustom] = useState({ from: resolved.fromDay, to: resolved.toDay })
  const [error, setError] = useState<string | null>(null)

  // The URL wins on arrival, once, so a bookmarked or shared link opens the
  // range it was saved with rather than whatever this session last looked at.
  const adopted = useRef(false)
  useEffect(() => {
    if (adopted.current) {
      return
    }
    adopted.current = true
    const fromUrl = readRangeParams(search)
    if (fromUrl !== null && validateRangeSelection(fromUrl).ok) {
      setSelection(fromUrl)
    }
  }, [search, setSelection])

  // Presets move the pickers; the pickers are what a custom range is made of.
  useEffect(() => {
    setCustom({ from: resolved.fromDay, to: resolved.toDay })
  }, [resolved.fromDay, resolved.toDay])

  function choose(preset: RangePreset) {
    setError(null)
    setSelection({ kind: 'preset', preset })
    setSearch(writeRangeParams(search, { kind: 'preset', preset }), { replace: true })
  }

  function applyCustom(next: { from: string; to: string }) {
    setCustom(next)
    const candidate = { kind: 'custom', fromDay: next.from, toDay: next.to } as const
    const checked = validateRangeSelection(candidate)
    if (!checked.ok) {
      // Reported, never swapped: reversing the dates hides the typo in the
      // field that caused it, and the user gets a range they did not ask for
      // with no clue which end was wrong.
      setError(checked.errors[0]?.message ?? 'That range does not work.')
      return
    }
    setError(null)
    setSelection(candidate)
    setSearch(writeRangeParams(search, candidate), { replace: true })
  }

  const days = rangeDays(resolved)
  const current = selection.kind === 'preset' ? selection.preset : null
  const mainValue = current !== null && MAIN.includes(current) ? current : null
  const choices: Choice<RangePreset>[] = MAIN.map((preset) => ({
    value: preset,
    label: shortLabel(preset),
    name: presetLabel(preset),
  }))
  const Chooser = look === 'tabs' ? Tabs : Segmented

  return (
    <div className={styles['range']} data-bleed>
      <Chooser label="Date range" choices={choices} value={mainValue} onChange={choose} />

      <details className={styles['other']} open={mainValue === null ? true : undefined}>
        <summary className={styles['otherSummary']}>Other dates</summary>
        <div className={styles['otherBody']}>
          <div className={styles['presets']} role="group" aria-label="Other periods">
            {OTHER.map((preset) => (
              <button
                key={preset}
                type="button"
                className={styles['preset']}
                aria-pressed={current === preset}
                onClick={() => {
                  choose(preset)
                }}
              >
                {presetLabel(preset)}
              </button>
            ))}
          </div>
          <div className={styles['custom']}>
            <label className={styles['dayField']}>
              <span>From</span>
              <input
                type="date"
                value={custom.from}
                aria-label="Range start"
                onChange={(event) => {
                  applyCustom({ ...custom, from: event.target.value })
                }}
              />
            </label>
            <label className={styles['dayField']}>
              <span>To</span>
              <input
                type="date"
                value={custom.to}
                aria-label="Range end"
                onChange={(event) => {
                  applyCustom({ ...custom, to: event.target.value })
                }}
              />
            </label>
          </div>
        </div>
      </details>

      {error !== null && (
        <p className={styles['error']} role="alert">
          {error}
        </p>
      )}

      <p className={styles['resolved']} data-testid="resolved-range">
        <strong>{resolved.label}</strong>
        {' · '}
        {formatDay(resolved.fromDay)} to {formatDay(resolved.toDay)}
        {' · '}
        <span className={styles['days']}>
          {days === 1 ? '1 day' : `${String(days)} days`}
        </span>
      </p>

      {empty && (
        <p className={styles['noData']} role="status">
          Nothing was recorded in this range.
        </p>
      )}
    </div>
  )
}
